"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createAbsenceHoursGetter = void 0;
exports.getNominalTime = getNominalTime;
exports.getNominalBonus = getNominalBonus;
exports.getVacationEntitlement = getVacationEntitlement;
const openapi_1 = require("@pentacode/openapi");
const model_1 = require("../model");
const utils_1 = require("../utils");
const holidays_1 = require("../holidays");
/**
 * Creates a getter to retrieve absence hours for a given date.
 *
 * If there is no exact match, it returns the closest preceeding value,
 * or undefined if there is no match within the last 13 weeks.
 *
 * This is relevant for calculating nominal hours when the `absenceMode` is set to `Average`
 * and the `nominalHoursMode` is set to `ExactWithoutHolidaysUsingAbsenceHours`.
 */
const createAbsenceHoursGetter = (results) => {
    const sortedResults = results.sort((a, b) => a.date.localeCompare(b.date));
    return (date) => {
        let closest = undefined;
        for (const result of sortedResults) {
            if (result.date <= date) {
                if (!closest || result.date > closest.date) {
                    closest = result;
                }
            }
        }
        if (closest && closest.date < (0, utils_1.dateAdd)(date, { weeks: -13 })) {
            console.warn(`No average hours found for date ${date}. Closest result is from ${closest.date}, which is more than 13 weeks ago.`);
            return undefined;
        }
        return closest === null || closest === void 0 ? void 0 : closest.averages["13weeks"].base.duration;
    };
};
exports.createAbsenceHoursGetter = createAbsenceHoursGetter;
function getNominalTime(company, emp, { from, to }, getAverageDailyWorkHours) {
    var _a;
    const startLedgers = company.settings.startLedgers;
    const contracts = emp.contracts.filter((c) => c.start <= to && (!c.end || c.end > from));
    const defaultPosition = emp.positions[0];
    const venue = (defaultPosition &&
        company.venues.find((v) => v.departments.some((d) => d.positions.some((p) => p.id === defaultPosition.id)))) ||
        company.venues[0];
    const holidays = (venue === null || venue === void 0 ? void 0 : venue.enabledHolidays) || undefined;
    const weekFactor = company.settings.weekFactor || 4.35;
    let total = 0;
    for (const contract of contracts) {
        const mode = contract.nominalHoursMode;
        let start = from >= contract.start ? from : contract.start;
        if (startLedgers && startLedgers > start) {
            start = startLedgers;
        }
        const end = !contract.end || contract.end > to ? to : contract.end;
        if (start > end) {
            start = end;
        }
        switch (mode) {
            case model_1.NominalHoursMode.WeekFactor:
                while (start < end) {
                    const { from, to } = (0, utils_1.getRange)(start, "month");
                    const subEnd = to < end ? to : end;
                    const fraction = ((0, utils_1.dateSub)(start, subEnd) / (0, utils_1.dateSub)(from, to));
                    total = (0, openapi_1.add)(total, (0, openapi_1.multiplyWithFactor)((0, openapi_1.multiplyWithFactor)(contract.hoursPerWeek, weekFactor), fraction));
                    start = subEnd;
                }
                break;
            case model_1.NominalHoursMode.Exact: {
                total = (0, openapi_1.add)(total, getNominalHoursInRange(contract.hoursPerWeek, start, end).nominalHoursTotal);
                break;
            }
            case model_1.NominalHoursMode.ExactWithoutHolidays: {
                const { nominalHoursTotal, nominalHoursPerDay } = getNominalHoursInRange(contract.hoursPerWeek, start, end);
                const holidaysInRange = (0, holidays_1.getHolidaysInRange)(start, end, { holidays, country: company.country }).length;
                const deductionHours = holidaysInRange * nominalHoursPerDay;
                total = (0, openapi_1.add)(total, nominalHoursTotal - deductionHours);
                break;
            }
            case model_1.NominalHoursMode.ExactWithoutHolidaysUsingAbsenceHours: {
                const { nominalHoursTotal, nominalHoursPerDay } = getNominalHoursInRange(contract.hoursPerWeek, start, end);
                const holidaysInRange = (0, holidays_1.getHolidaysInRange)(start, end, { holidays, country: company.country }).length;
                const deductionHoursPerDay = getDeductionHoursPerDay(contract, getAverageDailyWorkHours, start) || nominalHoursPerDay;
                total = (0, openapi_1.add)(total, (nominalHoursTotal - holidaysInRange * deductionHoursPerDay));
                break;
            }
            case model_1.NominalHoursMode.FixedDays:
            case model_1.NominalHoursMode.FixedDaysWithoutHolidays: {
                let day = start;
                while (day < end) {
                    const date = (0, utils_1.parseDateString)(day);
                    const hours = ((_a = contract.hoursPerDay) === null || _a === void 0 ? void 0 : _a[(date.getDay() + 6) % 7]) || 0;
                    if (mode === model_1.NominalHoursMode.FixedDays ||
                        !(0, holidays_1.isHoliday)(date.getFullYear(), date.getMonth(), date.getDate(), {
                            holidays,
                            country: company.country,
                        })) {
                        total = (0, openapi_1.add)(total, hours);
                    }
                    day = (0, utils_1.dateAdd)(day, { days: 1 });
                }
                break;
            }
        }
    }
    return total;
}
function getDeductionHoursPerDay(contract, getAverageDailyWorkHours, referenceDate) {
    if (contract.absenceMode === model_1.AbsenceMode.Average) {
        return getAverageDailyWorkHours === null || getAverageDailyWorkHours === void 0 ? void 0 : getAverageDailyWorkHours(referenceDate);
    }
    else if (contract.absenceMode === model_1.AbsenceMode.Fixed) {
        return contract.absenceHours;
    }
}
function getNominalHoursInRange(weeklyHours, start, end) {
    const nominalHoursPerDay = (weeklyHours / 7);
    const daysInRange = (0, utils_1.dateSub)(start, end);
    const nominalHoursTotal = (nominalHoursPerDay * daysInRange);
    return { nominalHoursTotal, nominalHoursPerDay };
}
function getNominalBonus(emp, { from, to }) {
    const contracts = emp.contracts.filter((c) => c.start <= to && (!c.end || c.end > from));
    let total = 0;
    for (const contract of contracts) {
        let start = from >= contract.start ? from : contract.start;
        const end = !contract.end || contract.end > to ? to : contract.end;
        if (start > end) {
            start = end;
        }
        while (start < end) {
            const { from, to } = (0, utils_1.getRange)(start, "month");
            const subEnd = to < end ? to : end;
            const fraction = ((0, utils_1.dateSub)(start, subEnd) / (0, utils_1.dateSub)(from, to));
            total = (0, openapi_1.add)(total, (0, openapi_1.multiplyWithFactor)(contract.sfnAdvance, fraction));
            start = subEnd;
        }
    }
    return total;
}
function getVacationEntitlement(balance) {
    var _a, _b;
    if (!balance) {
        return {
            available: 0,
            taken: 0,
            remaining: 0,
        };
    }
    const lastBalance = ((_a = balance.subBalances) === null || _a === void 0 ? void 0 : _a.length) ? balance.subBalances[balance.subBalances.length - 1] : balance;
    const available = (0, openapi_1.add)((_b = lastBalance.reset) !== null && _b !== void 0 ? _b : lastBalance.carry, lastBalance.nominal);
    const taken = lastBalance.actual;
    return {
        available,
        taken,
        remaining: (0, openapi_1.subtract)(available, taken),
    };
}
//# sourceMappingURL=nominals.js.map